<template>
  <div class="sp-textarea" :class="classModifiers">
    <textarea
      ref="textarea"
      part="field"
      class="sp-textarea__field"
      :data-testid="inputTestId"
      :aria-label="ariaLabel"
      :value="text"
      :rows="rows"
      :cols.attr="cols"
      :wrap.attr="wrapMode"
      :maxlength.attr="maxlength"
      :minlength.attr="minlength"
      :placeholder="placeholder"
      :autocomplete.attr="autocomplete"
      :autocapitalize.attr="attrs.autocapitalize"
      :spellcheck.attr="spellcheckAttribute"
      :dirname.attr="dirname"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      @input.stop="onInput"
      @focus="onFocus"
      @blur="onBlur"
    ></textarea>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, toRef, useAttrs, useTemplateRef, watch, watchPostEffect } from "vue";

import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { formAssociatedHost } from "#ce/framework/utils";
import { t } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean, toNumber } from "#ce/utils/props";
import { useTextModel } from "#ce/composables/useTextModel";
import { useTextareaAutoGrow } from "./composables/useTextareaAutoGrow";
import { useTextareaHostApi } from "./composables/useTextareaHostApi";
import { DEFAULT_ROWS, MIN_ROWS, props as propsDefinitions } from "./props.js";

// sp-textarea is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
// `delegatesFocus` puts focus on the inner textarea, so clicking an associated <label> and calling
// `focus()` on the element reach the field.
defineOptions(formAssociatedHost({ shadowRootOptions: { delegatesFocus: true }, inheritAttrs: false }));

defineEmits(["input"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(propsDefinitions);
const attrs = useAttrs();

// The largest value an integer content attribute may carry; past it the attribute is ignored.
const MAX_INTEGER_ATTRIBUTE = 2147483647;

const host = useHostElement();
const { internals, setValidity, mirrorFormValue, onFormReset, customError, name: formName } = useForm();

/**
 * The textarea element of the component.
 * @type {import('vue').Ref<HTMLTextAreaElement|null>}
 */
const textarea = useTemplateRef("textarea");

// The focusable control lives in the shadow root, where the <label> naming the host does not
// reach it. The labels are read once the element has rendered, by which time the markup around
// it has been parsed.
const labelElements = ref([]);
onMounted(() => (labelElements.value = [...(internals?.labels ?? [])]));

const { ariaLabel } = useAriaLabel(textarea, {
  label: () => attrs.ariaLabel,
  labelledby: toRef(props, "ariaLabelledby"),
  elements: labelElements,
});

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });
const readonly = computed(() => isPresent(props.readonly));
const required = computed(() => isPresent(props.required));
const autoGrow = computed(() => toBoolean(props.autoGrow));
const rows = computed(() => toRowCount(props.rows) ?? DEFAULT_ROWS);
const maxRows = computed(() => toRowCount(props.maxRows));
const cols = computed(() => toIntegerAttribute(props.cols, 1));
// A prop validator only warns, and warnings are stripped in production, so the value the inner
// control receives is narrowed here: `hard` folds the displayed line breaks into the submitted
// value, which this element does not reproduce.
const wrapMode = computed(() => (props.wrap === "off" ? "off" : undefined));

// The platform reads these as integers and ignores anything outside its own range, so a value it
// would ignore must not reach the field: the length constraints sit on ElementInternals and would
// block a form the native control lets through, and `cols` would collapse the rendered width.
const maxlength = computed(() => toIntegerAttribute(props.maxlength, 0));
const minlength = computed(() => toIntegerAttribute(props.minlength, 0));

/**
 * Resolves an integer content attribute the way the platform parses one: leading digits win and
 * trailing characters are ignored, so `"2.5"` is 2. A value below `minimum`, or past the
 * unsigned-long range the platform allows, is dropped — the native control ignores those
 * entirely and falls back to its own default rather than enforcing a smaller one.
 *
 * `minimum` is 0 for the length constraints and 1 for `cols`, which the platform limits to
 * positive numbers.
 *
 * @param {Number|String|undefined} value
 * @param {Number} minimum
 * @returns {Number|undefined}
 */
function toIntegerAttribute(value, minimum) {
  const parsed = parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed < minimum || parsed > MAX_INTEGER_ATTRIBUTE) {
    return undefined;
  }

  return parsed;
}
const spellcheckAttribute = computed(() => {
  if (attrs.spellcheck === undefined) {
    return undefined;
  }

  return isPresent(attrs.spellcheck) ? "true" : "false";
});

/**
 * Resolves a row count, or undefined for anything that is not a whole number of
 * at least `MIN_ROWS`, leaving the caller its own default.
 *
 * @param {Number|String|undefined} value
 * @returns {Number|undefined}
 */
function toRowCount(value) {
  const count = Math.trunc(toNumber(value, NaN));

  return Number.isFinite(count) && count >= MIN_ROWS ? count : undefined;
}

// The element's child text seeds the field the way the parser seeds a native `<textarea>`, with a
// single leading newline dropped.
function seedFromChildText() {
  return (host?.textContent ?? "").replace(/^\r?\n/, "");
}

// The textarea's value is a string throughout, so an absent one is the empty string.
function toText(value) {
  return value === undefined || value === null ? "" : String(value);
}

const { text, defaultValue, isUserEdited, hasUserEdits, setText, setUserText, setDefaultValue, reset } = useTextModel({
  normalizeText: toText,
  normalizeDefault: toText,
  seedDefault: seedFromChildText,
});

// The submitted value is a string throughout, so an empty field submits "" the way a native
// `<textarea>` does instead of dropping out of the FormData. With `dirname` the entry is
// accompanied by the text direction, as a second entry under that name.
const formValue = computed(() => {
  if (!props.dirname || !formName.value) {
    return text.value;
  }

  const data = new FormData();
  data.append(formName.value, text.value);
  data.append(props.dirname, textDirection());

  return data;
});

function textDirection() {
  return textarea.value ? getComputedStyle(textarea.value).direction : "ltr";
}

// A native `<textarea>` fires `change` on blur, not per keystroke, so the element owns the
// event and the form value is kept in sync silently.
mirrorFormValue(formValue);

// A reset returns the field to its untouched state, failures included.
onFormReset(() => {
  reset();
  hasLeftAnEditedField.value = false;
  wasValidityReported.value = false;
});

// The attribute and the host's `setCustomValidity()` name one custom error, so the attribute
// writes through the same method rather than keeping a second copy. An absent attribute writes
// nothing at mount, leaving an error a consumer set before the component loaded; removing the
// attribute after it carried a value still clears.
watch(() => props.customValidity, applyCustomValidity, { immediate: props.customValidity !== undefined });

function applyCustomValidity(message) {
  host?.setCustomValidity?.(message ?? "");
}

// Read through the i18n instance rather than off a resolved promise, so the
// message follows the bundle arriving with a client's own wording.
const missingMessage = computed(() => props.requiredMessage || t("textarea_value_missing"));

// A readonly control is exempt from constraint validation, matching a native
// `<textarea readonly required>`. The platform bars a disabled one on its own.
//
// The length constraints follow the platform rule: they fail only a value the user last edited,
// and an empty field is never too short. Typing is capped at `maxlength` by the inner element, so
// `tooLong` is reachable only for a seeded value the user has since edited.
const validity = computed(() => {
  const value = text.value;

  if (readonly.value) {
    return { flags: {}, message: "" };
  }

  if (required.value && value === "") {
    return { flags: { valueMissing: true }, message: missingMessage.value };
  }

  if (isUserEdited.value && maxlength.value !== undefined && value.length > maxlength.value) {
    return { flags: { tooLong: true }, message: t("textarea_too_long", { maxlength: maxlength.value }) };
  }

  if (isUserEdited.value && value !== "" && minlength.value !== undefined && value.length < minlength.value) {
    return { flags: { tooShort: true }, message: t("textarea_too_short", { minlength: minlength.value }) };
  }

  return { flags: {}, message: "" };
});

function mirrorValidity() {
  setValidity(validity.value.flags, validity.value.message, textarea.value ?? undefined);
}

// Post-flush so the first run has the anchor the browser points its bubble at.
watchPostEffect(mirrorValidity);

useTextareaHostApi({
  textarea,
  text,
  defaultValue,
  labels: () => internals?.labels ?? null,
  setText,
  setDefaultValue,
});

useTextareaAutoGrow(textarea, { autoGrow, rows, maxRows, text });

// The field claims focus as the element renders, so an editor mounted on demand behaves like the
// control it stands in for.
onMounted(() => {
  if (isPresent(attrs.autofocus)) {
    textarea.value?.focus();
  }
});

// `:user-invalid` semantics: the failure surfaces once the user has left a field they worked on or
// the form has reported its validity, never on an untouched field at first paint.
const hasLeftAnEditedField = ref(false);
const wasValidityReported = ref(false);
useEventListener(host, "invalid", () => (wasValidityReported.value = true));

let editedSinceFocus = false;
let textAtFocus = "";

// The only path that emits `input`, matching a native control, which reports user input alone.
// The model is set first so `host.value` is current for a `v-model` consumer reading it here.
function onInput(event) {
  setUserText(event.target.value);
  editedSinceFocus = true;
  // The post-flush effect above runs after this dispatch, so the mirror is applied here too: a
  // listener reads the host's value, form entry, and validity as one coherent state.
  mirrorValidity();
  emitBubbling("input", text.value);
}

function onFocus() {
  editedSinceFocus = false;
  textAtFocus = text.value;
}

function onBlur() {
  // A native control fires `change` when the user leaves a field they changed, and stays silent
  // for a value a script wrote while the field held focus.
  if (editedSinceFocus && text.value !== textAtFocus) {
    host?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  hasLeftAnEditedField.value = hasLeftAnEditedField.value || hasUserEdits.value;
  editedSinceFocus = false;
}

const isInvalid = computed(() => Boolean(customError.value) || Object.keys(validity.value.flags).length > 0);
const showsInvalid = computed(() => isInvalid.value && (wasValidityReported.value || hasLeftAnEditedField.value));

const { isSmScreen } = useBreakpoints();

const classModifiers = computed(() => {
  const density = toBoolean(props.densitysmcompact) && isSmScreen.value ? "compact" : props.density;
  const modifiers = {
    "--is-invalid": showsInvalid.value,
    "--auto-grow": autoGrow.value,
    "--has-cols": cols.value !== undefined,
  };

  if (density !== "default") {
    modifiers[`--density-${density}`] = true;
  }

  if (props.variant !== "default") {
    modifiers[`--variant-${props.variant}`] = true;
  }

  return modifiers;
});
</script>

<style lang="scss">
:host {
  display: block;
  box-sizing: border-box;
}
:host(:focus) {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

// Disabled styling is driven by the native :disabled state (own attribute or an
// ancestor <fieldset disabled>), no JS required. The inner textarea's non-interactivity
// is handled separately via the bound disabled prop.
:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

<style lang="scss" scoped>
.sp-textarea {
  --border-color: var(--sp-comp-textarea-border-color, var(--sp-sys-form-field-border-color));
  --border-width: var(--sp-comp-textarea-border-width, var(--sp-sys-border-width-default));
  --textarea-padding-inline: var(--sp-comp-textarea-padding-inline, var(--sp-sys-form-field-padding-inline));
  --textarea-padding-block: var(--sp-comp-textarea-padding-block, var(--sp-ref-scale-8));
  --textarea-min-height: var(--sp-comp-textarea-min-height, var(--sp-sys-form-field-min-height));

  border-width: var(--border-width);
  border-style: var(--sp-comp-textarea-border-style, var(--sp-sys-form-field-border-style));
  border-color: var(--border-color);
  border-radius: var(--sp-comp-textarea-border-radius, var(--sp-sys-shape-control));
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: var(--sp-comp-textarea-min-width, 10ch);
  min-height: var(--textarea-min-height);
  background-color: var(--sp-comp-textarea-background-color, var(--sp-sys-form-field-background-color));

  // The field is the only focusable descendant, so the wrapper reads its focus straight from CSS.
  &:focus-within {
    --border-color: var(--sp-comp-textarea-focus-border-color, var(--sp-sys-form-field-focus-border-color));

    box-shadow: var(--sp-comp-textarea-focus-box-shadow, var(--sp-sys-form-field-focus-box-shadow));
    outline: var(--sp-comp-textarea-focus-outline, var(--sp-sys-form-field-focus-outline));
    outline-offset: var(--sp-comp-textarea-focus-outline-offset, var(--sp-sys-form-field-focus-outline-offset));
  }

  &.--is-invalid {
    --border-color: var(--sp-comp-textarea-invalid-border-color, var(--sp-sys-form-field-invalid-border-color));

    box-shadow: var(--sp-comp-textarea-invalid-box-shadow, var(--sp-sys-form-field-invalid-box-shadow));
    outline: var(--sp-comp-textarea-invalid-outline, var(--sp-sys-form-field-invalid-outline));
    outline-offset: var(--sp-comp-textarea-invalid-outline-offset, var(--sp-sys-form-field-invalid-outline-offset));
  }

  &.--density-compact {
    --textarea-min-height: var(--sp-comp-textarea-compact-min-height, var(--sp-sys-form-field-compact-min-height));
    --textarea-padding-inline: var(
      --sp-comp-textarea-compact-padding-inline,
      var(--sp-sys-form-field-compact-padding-inline)
    );
    --textarea-padding-block: var(--sp-comp-textarea-compact-padding-block, var(--sp-ref-scale-4));
  }

  &.--variant-plain {
    --border-width: 0;
    --textarea-padding-inline: var(--sp-comp-textarea-plain-padding-inline, 0);
    --textarea-padding-block: var(--sp-comp-textarea-plain-padding-block, 0);
  }

  &__field {
    box-sizing: border-box;
    display: block;
    flex: 1;
    width: 100%;
    margin: 0;
    padding-inline: var(--textarea-padding-inline);
    padding-block: var(--textarea-padding-block);
    border: none;
    outline: none;
    resize: var(--sp-comp-textarea-resize, vertical);
    background-color: transparent;
    font-family: var(--sp-comp-textarea-font-family, inherit);
    font-size: var(--sp-comp-textarea-font-size, var(--sp-sys-form-field-font-size));
    line-height: var(--sp-comp-textarea-line-height, var(--sp-sys-form-field-line-height));
    color: var(--sp-comp-textarea-color, var(--sp-sys-form-field-color));
    text-align: var(--sp-comp-textarea-text-align, var(--sp-sys-form-field-text-align));
    cursor: var(--sp-comp-textarea-cursor, text);

    &::placeholder {
      color: var(--sp-comp-textarea-placeholder-color, var(--sp-sys-form-field-placeholder-color));
    }
  }

  // The height is computed, so the resize handle would fight the measurement.
  &.--auto-grow &__field {
    resize: none;
  }

  // The column count sizes the field, so neither box may stretch to the container.
  &.--has-cols {
    width: fit-content;
    min-width: 0;
  }

  &.--has-cols &__field {
    width: auto;
  }
}
</style>
